import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listDoctors, listSpecializations } from "@/api/userApi";
import type { DoctorSummary } from "@/types/domain";

export default function DoctorDirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSpec = searchParams.get("specialization") ?? "";
  const [q, setQ] = useState("");
  const [specialization, setSpecialization] = useState(initialSpec);
  const [city, setCity] = useState("");
  const navigate = useNavigate();

  const { data: specs } = useQuery({
    queryKey: ["specializations"],
    queryFn: listSpecializations,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["doctors", { q, specialization, city }],
    queryFn: () => listDoctors({ q, specialization, city }),
  });

  const doctors = useMemo<DoctorSummary[]>(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : data.content;
  }, [data]);

  const applySpec = (v: string) => {
    setSpecialization(v);
    const next = new URLSearchParams(searchParams);
    if (v) next.set("specialization", v);
    else next.delete("specialization");
    setSearchParams(next, { replace: true });
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Find a doctor
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Search verified doctors across India and book a slot in minutes.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 4,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1.2fr 1fr auto" },
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search by name, specialty or symptom"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
        />
        <TextField
          select
          label="Specialization"
          value={specialization}
          onChange={(e) => applySpec(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {(specs ?? []).map((s) => (
            <MenuItem key={s.id} value={s.name}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
        <Button
          variant="contained"
          sx={{ minWidth: 120 }}
          onClick={() => {
            /* query refetches automatically on state changes */
          }}
        >
          Search
        </Button>
      </Paper>

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Unable to load doctors right now. Please try again in a moment.
          </Typography>
        </Paper>
      )}

      {!isLoading && !isError && doctors.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            No doctors match your filters
          </Typography>
          <Typography color="text.secondary">
            Try a specialty or a symptom, such as skin or heart, or clear the filters.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        {doctors.map((d) => (
          <Grid item xs={12} sm={6} md={4} key={d.id}>
            <Card
              onClick={() => navigate(`/doctors/${d.id}`)}
              sx={{ cursor: "pointer", height: "100%" }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={d.photoUrl}
                    sx={{
                      bgcolor: "primary.main",
                      width: 56,
                      height: 56,
                      fontWeight: 800,
                    }}
                  >
                    {d.fullName?.[0] ?? "D"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {d.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {d.specialization ?? "—"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} mt={2}>
                  <Rating
                    size="small"
                    value={d.averageRating ?? 0}
                    precision={0.1}
                    readOnly
                  />
                  <Typography variant="caption" color="text.secondary">
                    {(d.averageRating ?? 0).toFixed(1)} ({d.reviewCount ?? 0})
                  </Typography>
                </Stack>

                <Stack spacing={0.75} mt={2}>
                  {d.experienceYears != null && (
                    <Row
                      icon={<WorkHistoryIcon fontSize="small" />}
                      text={`${d.experienceYears} years experience`}
                    />
                  )}
                  {d.city && (
                    <Row
                      icon={<LocationOnIcon fontSize="small" />}
                      text={d.city}
                    />
                  )}
                  {d.consultationFee != null && (
                    <Row
                      icon={<PaymentsIcon fontSize="small" />}
                      text={`₹${d.consultationFee} / consult`}
                    />
                  )}
                </Stack>

                {d.languages && d.languages.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap" useFlexGap>
                    {d.languages.slice(0, 3).map((l) => (
                      <Chip key={l} label={l} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/doctors/${d.id}`);
                  }}
                >
                  View & book
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

function Row({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Stack>
  );
}
